import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API, { refreshSession } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  PaperPlaneTilt, Robot, User, ArrowClockwise, ChatCircleText,
  Plus, CaretRight, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI Assistant page — tenant-scoped GPT-5.2 chat with:
 *   - Streaming responses (SSE) with typing indicator
 *   - Persistent session history (sidebar list, resume by click)
 *   - Quick-action chips the LLM emits (navigate / create flows)
 *   - Keyboard shortcuts: Enter to send, Shift+Enter newline
 */
export default function AIChatPage() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [streamingText, setStreamingText] = useState(""); // current assistant response being streamed
  const [sessionId, setSessionId] = useState(() => `chat-${Date.now()}`);
  const [sessions, setSessions] = useState([]);
  const [sessionsOpen, setSessionsOpen] = useState(true);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingText]);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await API.get("/ai/sessions", { params: { limit: 20 } });
      setSessions(data?.sessions || []);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  const openSession = async (sid) => {
    if (sending) return;
    setSessionId(sid);
    setMessages([]);
    try {
      const { data } = await API.get(`/ai/sessions/${sid}`);
      setMessages((data?.messages || []).map(m => ({
        role: m.role, content: m.content, actions: m.actions || [],
      })));
    } catch {
      toast.error("Could not load chat");
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm("Delete this chat?")) return;
    try {
      await API.delete(`/ai/sessions/${sid}`);
      setSessions(s => s.filter(x => x.session_id !== sid));
      if (sid === sessionId) newChat();
      toast.success("Chat deleted");
    } catch {
      toast.error("Could not delete chat");
    }
  };

  const newChat = () => {
    if (sending && abortRef.current) abortRef.current.abort();
    setMessages([]);
    setStreamingText("");
    setSessionId(`chat-${Date.now()}`);
  };

  const sendStream = async (userMsg) => {
    setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    setSending(true);
    setStreamingText("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const start = () => fetch(`${API_ROOT}/ai/chat/stream`, {
        method: "POST",
        signal: controller.signal,
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: userMsg, session_id: sessionId }),
      });
      let resp = await start();
      if (resp.status === 401) {  // stale access cookie — refresh the session once and retry
        try { await refreshSession(); resp = await start(); } catch {}
      }
      if (!resp.ok || !resp.body) {
        throw new Error(`HTTP ${resp.status}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      let finalActions = [];

      // SSE parsing loop
      /* eslint-disable no-await-in-loop */
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const chunks = buffer.split("\n\n");
        buffer = chunks.pop() || "";
        for (const raw of chunks) {
          const lines = raw.split("\n");
          let event = "message";
          let dataRaw = "";
          for (const ln of lines) {
            if (ln.startsWith("event:")) event = ln.slice(6).trim();
            else if (ln.startsWith("data:")) dataRaw += ln.slice(5).trim();
          }
          if (!dataRaw) continue;
          if (event === "chunk") {
            try {
              const parsed = JSON.parse(dataRaw);
              if (parsed.text) {
                assistantText += parsed.text;
                setStreamingText(assistantText);
              }
            } catch { /* ignore malformed chunk */ }
          } else if (event === "actions") {
            try { finalActions = JSON.parse(dataRaw) || []; } catch { /* ignore */ }
          } else if (event === "error") {
            throw new Error(dataRaw);
          }
        }
      }
      /* eslint-enable no-await-in-loop */

      // Commit the streamed message
      setMessages(prev => [...prev, { role: "assistant", content: assistantText, actions: finalActions }]);
      setStreamingText("");
      loadSessions();
    } catch (err) {
      if (err.name !== "AbortError") {
        setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm temporarily unavailable. Please try again.", actions: [] }]);
        toast.error("AI service unavailable");
      }
      setStreamingText("");
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const handleSend = (e) => {
    e?.preventDefault();
    const msg = input.trim();
    if (!msg || sending) return;
    setInput("");
    sendStream(msg);
  };

  const handleAction = (action) => {
    if (!action) return;
    if (action.action === "navigate" && action.url) {
      navigate(action.url);
      return;
    }
    const map = {
      create_job: "/jobs?create=1",
      create_task: "/projects?create_task=1",
      create_quote: "/quotes?create=1",
      create_project: "/projects?create=1",
      find_supplier: "/suppliers/nearby",
    };
    const url = map[action.action];
    if (url) navigate(url);
  };

  return (
    <div data-testid="ai-chat-page" className="flex gap-4" style={{ height: "calc(100vh - 130px)" }}>
      {/* Sessions sidebar */}
      <aside className={`hidden lg:flex flex-col w-60 shrink-0 border border-border rounded-md bg-card ${sessionsOpen ? "" : "opacity-50"}`}>
        <div className="flex items-center justify-between p-3 border-b border-border">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Chats</p>
          <button
            onClick={newChat}
            className="text-xs px-2 py-1 rounded hover:bg-muted flex items-center gap-1"
            data-testid="sidebar-new-chat"
          >
            <Plus size={12} /> New
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1">
          {sessions.length === 0 ? (
            <p className="text-[11px] text-muted-foreground px-2 py-4 text-center">No saved chats yet</p>
          ) : sessions.map(s => (
            <div
              key={s.session_id}
              role="button"
              tabIndex={0}
              onClick={() => openSession(s.session_id)}
              onKeyDown={(e) => { if (e.key === "Enter") openSession(s.session_id); }}
              className={`group flex items-start gap-2 p-2 rounded text-xs cursor-pointer hover:bg-muted transition ${s.session_id === sessionId ? "bg-muted" : ""}`}
              data-testid={`session-${s.session_id}`}
            >
              <ChatCircleText size={14} className="mt-0.5 shrink-0 text-muted-foreground" />
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{s.title}</p>
                <p className="text-[10px] text-muted-foreground truncate">{s.last_preview}</p>
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteSession(s.session_id); }}
                className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                data-testid={`delete-session-${s.session_id}`}
                title="Delete chat"
              >
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      {/* Chat panel */}
      <div className="flex-1 flex flex-col min-w-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-[Chivo] tracking-tight">AI Assistant</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Powered by GPT-5.2 — grounded in your tenant's live data</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              className="lg:hidden text-xs px-2 py-1 rounded border border-border"
              onClick={() => setSessionsOpen(o => !o)}
              data-testid="toggle-sessions-btn"
            >
              Chats
            </button>
            <Button variant="outline" size="sm" onClick={newChat} data-testid="new-chat-btn">
              <ArrowClockwise size={14} className="mr-1" /> New Chat
            </Button>
          </div>
        </div>

        <div className="flex-1 border border-border rounded-md flex flex-col overflow-hidden bg-card">
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.length === 0 && !streamingText && (
              <div className="flex flex-col items-center justify-center h-full text-center py-12">
                <Robot size={48} className="text-muted-foreground mb-3 opacity-50" />
                <h3 className="text-base font-semibold mb-1">How can I help?</h3>
                <p className="text-xs text-muted-foreground max-w-sm mb-4">
                  Ask about your overdue tasks, active emergencies, expiring compliance documents, or what to do next — I can see the live numbers for your tenant.
                </p>
                <div className="flex flex-wrap gap-2 justify-center max-w-md">
                  {[
                    "What needs my attention today?",
                    "Any compliance docs expiring soon?",
                    "Find a plumber near my active emergency",
                    "Summarise open jobs by trade",
                  ].map(q => (
                    <button
                      key={q}
                      onClick={() => { setInput(q); }}
                      className="text-xs px-3 py-1.5 rounded-full border border-border hover:bg-muted transition-colors"
                      data-testid={`suggestion-${q.slice(0, 10)}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${idx}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-lg ${msg.role === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    {msg.role === "user" ? <User size={12} /> : <Robot size={12} />}
                    <span className="text-[10px] font-semibold opacity-70">{msg.role === "user" ? "You" : "AI Assistant"}</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{msg.content}</div>
                  {msg.role === "assistant" && Array.isArray(msg.actions) && msg.actions.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-border/60" data-testid={`chat-actions-${idx}`}>
                      {msg.actions.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => handleAction(a)}
                          className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors font-medium"
                          data-testid={`chat-action-btn-${idx}-${i}`}
                        >
                          {a.label} <CaretRight size={10} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Live streaming chunk */}
            {streamingText && (
              <div className="flex justify-start" data-testid="chat-streaming">
                <div className="max-w-[80%] px-4 py-3 rounded-lg bg-muted">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Robot size={12} />
                    <span className="text-[10px] font-semibold opacity-70">AI Assistant</span>
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{streamingText}<span className="inline-block w-1.5 h-4 bg-current ml-0.5 align-middle animate-pulse" /></div>
                </div>
              </div>
            )}

            {/* Pre-stream thinking state */}
            {sending && !streamingText && (
              <div className="flex justify-start">
                <div className="bg-muted px-4 py-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                    <span className="text-xs text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={scrollRef} />
          </div>

          <form onSubmit={handleSend} className="p-3 border-t border-border flex gap-2">
            <Input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ask me anything..."
              className="flex-1"
              disabled={sending}
              data-testid="ai-chat-input"
              autoFocus
            />
            <Button type="submit" disabled={sending || !input.trim()} data-testid="ai-chat-send">
              <PaperPlaneTilt size={16} />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
